import { makeBetterEnum } from './betterEnum';

/**
 * These are meaningful intervals of time, the duration of which we monitor
 * in order to determine the user's perceived experience of FE loading time.
 *
 * See https://whimsical.com/front-end-loading-events-and-metrics-6FcTMWkgZY22LphvcBtWwB
 *
 * EVENTS occur in this order:
 * ...TTFB occurs before
 * 1. FirstContentfulPaint (FCP): The first UI is rendered  (signal of any loading)
 *    Ex. usually full-page loading spinner
 *
 * 2. FirstSkeletonPaint (FSP): the first render of UI (signal of successful loading)
 *    Ex. header, skeleton card with child loading spinner (may align with TTI)
 *
 * 3. FirstMeaningfulPaint (FMP): ~50% users can use page as intended
 *    Ex. user can upload file but BI charts are still loading
 *
 * 4. LastMeaningfulPaint (LMP): ~90% users can use page as intended
 *    Ex. user can upload file + BI charts loaded, but template downloader still loads
 * ...LCP occurs after
 *
 * INTERVALS occur in this order:
 *
 * 1. FirstLoadingInterval (This may or may not be the same point as FCP)
 *    Duration between FirstContentfulPaint & FirstSkeletonPaint
 *    Ex. page loading spinner -> header
 *
 * 2. FirstMeaningfulInterval
 *    Duration between FirstSkeletonPaint and FirstMeaningfulPaint
 *    Ex. header -> useful UI rendered with significant loading spinners
 *
 * 3. LastMeaningfulInterval
 *    Duration between FirstMeaningfulPaint and LastMeaningfulPaint
 *    Ex. user can upload file + BI charts loaded, but template downloader still loads
 */
export const MeaningfulPaintIntervalType = makeBetterEnum(
  // Duration between FirstContentfulPaint & FirstSkeletonPaint
  'FirstLoadingInterval',
  // Duration between FirstSkeletonPaint and FirstMeaningfulPaint
  'FirstMeaningfulInterval',
  // Duration between FirstMeaningfulPaint and LastMeaningfulPaint
  'LastMeaningfulInterval'
);

export type MeaningfulPaintIntervalType =
  (typeof MeaningfulPaintIntervalType)[keyof typeof MeaningfulPaintIntervalType];

/**
 * In observability logging, this allows us to differentiate data when we're
 * releasing a new version or comparing between two options. Calling A/B
 * instead of 1/2 so we can use for lateral comparisons, not just rewrites.
 */
export const PageVersionForLogging = makeBetterEnum('VersionA', 'VersionB');

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PageVersionForLogging =
  (typeof PageVersionForLogging)[keyof typeof PageVersionForLogging];
